/**
 * BRD Rule Schema Configuration
 * Defines standard sections, fields, and their relationships
 */

export interface RuleFieldConfig {
  value: string
  label: string
  description: string
  validationType: 'string' | 'number' | 'dimensions' | 'fileSize' | 'duration' | 'regex' | 'list'
  applicableChannels?: string[]
}

export interface RuleSectionConfig {
  value: string
  label: string
  description: string
  fields: string[] // References to field values
  icon?: string
}

// Standard field definitions
export const RULE_FIELDS: Record<string, RuleFieldConfig> = {
  dimensions: {
    value: 'dimensions',
    label: 'Dimensions',
    description: 'Asset width x height in pixels',
    validationType: 'dimensions',
    applicableChannels: ['Display', 'Social', 'Email']
  },
  fileSize: {
    value: 'fileSize',
    label: 'File Size',
    description: 'Maximum file size in KB or MB',
    validationType: 'fileSize',
    applicableChannels: ['Display', 'Social', 'Email', 'Video']
  },
  format: {
    value: 'format',
    label: 'File Format',
    description: 'Allowed file formats (JPG, PNG, MP4, etc.)',
    validationType: 'list',
    applicableChannels: ['Display', 'Social', 'Email', 'Video']
  },
  duration: {
    value: 'duration',
    label: 'Video Duration',
    description: 'Video length in seconds',
    validationType: 'duration',
    applicableChannels: ['Video', 'OTT']
  },
  aspectRatio: {
    value: 'aspectRatio',
    label: 'Aspect Ratio',
    description: 'Width:height ratio (16:9, 4:3, 1:1)',
    validationType: 'string',
    applicableChannels: ['Video', 'Social']
  },
  frameRate: {
    value: 'frameRate',
    label: 'Frame Rate',
    description: 'Frames per second (fps)',
    validationType: 'number',
    applicableChannels: ['Video', 'OTT']
  },
  bitrate: {
    value: 'bitrate',
    label: 'Bitrate',
    description: 'Video/audio bitrate in Mbps',
    validationType: 'number',
    applicableChannels: ['Video', 'OTT']
  },
  codec: {
    value: 'codec',
    label: 'Codec',
    description: 'Video codec (H.264, H.265, VP9)',
    validationType: 'list',
    applicableChannels: ['Video', 'OTT']
  },
  typography: {
    value: 'typography',
    label: 'Typography',
    description: 'Font size, readability requirements',
    validationType: 'string',
    applicableChannels: ['Display', 'Social', 'Email', 'Video']
  },
  contrast: {
    value: 'contrast',
    label: 'Contrast Ratio',
    description: 'WCAG contrast requirements',
    validationType: 'number',
    applicableChannels: ['Display', 'Social', 'Email']
  },
  safetyInfo: {
    value: 'safetyInfo',
    label: 'Safety Information',
    description: 'Required safety/warning text',
    validationType: 'string',
    applicableChannels: ['All']
  },
  fairBalance: {
    value: 'fairBalance',
    label: 'Fair Balance',
    description: 'Benefit/risk balance requirements',
    validationType: 'string',
    applicableChannels: ['All']
  },
  indication: {
    value: 'indication',
    label: 'Indication',
    description: 'Product indication statement',
    validationType: 'string',
    applicableChannels: ['All']
  },
  disclaimers: {
    value: 'disclaimers',
    label: 'Disclaimers',
    description: 'Required disclaimers',
    validationType: 'string',
    applicableChannels: ['All']
  },
  fdaFilingNumber: {
    value: 'fdaFilingNumber',
    label: 'FDA Filing Number',
    description: 'FDA promotional review number',
    validationType: 'string',
    applicableChannels: ['All']
  },
  customerType: {
    value: 'customerType',
    label: 'Customer Type',
    description: 'HCP, DTC, or Payer audience',
    validationType: 'list',
    applicableChannels: ['All']
  },
  brandGuidelines: {
    value: 'brandGuidelines',
    label: 'Brand Guidelines',
    description: 'Brand-specific requirements',
    validationType: 'string',
    applicableChannels: ['All']
  },
  accessibility: {
    value: 'accessibility',
    label: 'Accessibility',
    description: 'WCAG/508 compliance',
    validationType: 'string',
    applicableChannels: ['Display', 'Social', 'Email', 'Video']
  },
  naming: {
    value: 'naming',
    label: 'File Naming',
    description: 'Naming convention rules',
    validationType: 'regex',
    applicableChannels: ['All']
  },
  metadata: {
    value: 'metadata',
    label: 'Metadata',
    description: 'Required metadata fields',
    validationType: 'string',
    applicableChannels: ['All']
  }
}

// Standard section definitions
export const RULE_SECTIONS: Record<string, RuleSectionConfig> = {
  displayRequirements: {
    value: 'Display Requirements',
    label: 'Display Requirements',
    description: 'Banner ads and display creative specifications',
    fields: ['dimensions', 'fileSize', 'format']
  },
  videoRequirements: {
    value: 'Video Requirements',
    label: 'Video Requirements',
    description: 'Video asset specifications',
    fields: ['duration', 'dimensions', 'fileSize', 'format', 'aspectRatio', 'frameRate', 'bitrate', 'codec']
  },
  compliance: {
    value: 'Compliance',
    label: 'Compliance',
    description: 'Regulatory and legal requirements',
    fields: ['safetyInfo', 'fairBalance', 'indication', 'disclaimers', 'fdaFilingNumber', 'customerType']
  },
  branding: {
    value: 'Branding',
    label: 'Branding',
    description: 'Brand guidelines and visual identity',
    fields: ['brandGuidelines', 'typography', 'contrast']
  },
  accessibility: {
    value: 'Accessibility',
    label: 'Accessibility',
    description: 'WCAG and 508 compliance requirements',
    fields: ['accessibility', 'contrast', 'typography']
  },
  technical: {
    value: 'Technical',
    label: 'Technical Specifications',
    description: 'File format and technical requirements',
    fields: ['format', 'fileSize', 'codec', 'bitrate', 'naming', 'metadata']
  },
  social: {
    value: 'Social Media',
    label: 'Social Media',
    description: 'Social platform requirements',
    fields: ['dimensions', 'fileSize', 'format', 'duration', 'aspectRatio']
  },
  email: {
    value: 'Email',
    label: 'Email Marketing',
    description: 'Email creative requirements',
    fields: ['dimensions', 'fileSize', 'format', 'accessibility']
  },
  customRules: {
    value: 'Custom Rules',
    label: 'Custom Rules',
    description: 'Organization-specific rules',
    fields: [] // Can include any field
  }
}

// Severity levels
export const SEVERITY_LEVELS = [
  { value: 'critical', label: 'Critical', color: 'red', description: 'Must be fixed before deployment' },
  { value: 'warning', label: 'Warning', color: 'yellow', description: 'Should be reviewed' },
  { value: 'minor', label: 'Minor', color: 'blue', description: 'Nice to have' },
  { value: 'info', label: 'Info', color: 'gray', description: 'Informational only' }
] as const

// Channel types
export const CHANNEL_TYPES = [
  { value: 'All', label: 'All Channels' },
  { value: 'Display', label: 'Display Advertising' },
  { value: 'Social', label: 'Social Media' },
  { value: 'Video', label: 'Video/OTT' },
  { value: 'Email', label: 'Email Marketing' },
  { value: 'Print', label: 'Print Materials' },
  { value: 'Mobile', label: 'Mobile Apps' },
  { value: 'Web', label: 'Website' }
] as const

// Helper functions
export function getFieldsForSection(sectionValue: string): RuleFieldConfig[] {
  const section = Object.values(RULE_SECTIONS).find(s => s.value === sectionValue)
  if (!section) return []
  
  return section.fields
    .map(fieldValue => RULE_FIELDS[fieldValue])
    .filter(Boolean)
}

export function getSuggestedFieldsForChannel(channel: string): RuleFieldConfig[] {
  return Object.values(RULE_FIELDS).filter(field => 
    !field.applicableChannels || 
    field.applicableChannels.includes(channel) ||
    field.applicableChannels.includes('All')
  )
}

export function validateRuleConfiguration(rule: any): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  
  if (!rule.rule || rule.rule.trim().length === 0) {
    errors.push('Rule description is required')
  }
  
  if (!rule.severity) {
    errors.push('Severity is required')
  }
  
  if (!rule.field) {
    errors.push('Field is required')
  }
  
  if (!rule.section) {
    errors.push('Section is required')
  }
  
  return {
    valid: errors.length === 0,
    errors
  }
}

// Export lists for dropdowns
export const SECTIONS_LIST = Object.values(RULE_SECTIONS).map(s => ({
  value: s.value,
  label: s.label
}))

export const FIELDS_LIST = Object.values(RULE_FIELDS).map(f => ({
  value: f.value,
  label: f.label,
  description: f.description
}))

export const SEVERITY_LIST = SEVERITY_LEVELS.map(s => ({
  value: s.value,
  label: s.label,
  color: s.color
}))

export const CHANNELS_LIST = CHANNEL_TYPES.map(c => ({
  value: c.value,
  label: c.label
}))

